// Socket.IO WebSocket Test Script with Order Functionality
import { getAccessToken } from "../utils/tokens";
import { io } from 'socket.io-client';
import { v4 as uuidv4 } from "uuid";
import { API_ENDPOINTS } from "../utils/constants";

// Type definitions for orders
export interface OrderData {
  symbol: string;
  side: 'buy' | 'sell';
  type: 'market' | 'limit';
  size?: string;
  price?: string;
  clientOid?: string;
  timeInForce?: string;
  cancelAfter?: number;
  postOnly?: boolean;
  hidden?: boolean;
  iceberg?: boolean;
  visibleSize?: string;
  remark?: string;
  stop?: 'loss' | 'entry';
  stopPrice?: string;
  stopPriceType?: 'TP' | 'IP' | 'MP';
  funds?: string;
}

export interface CancelOrderData {
  orderId?: string;
  clientOid?: string;
  symbol: string;
}

export interface WebSocketResponse {
  success: boolean;
  data?: any;
  error?: string;
  timestamp: number;
}

export interface ConnectionStatus {
  authenticated: boolean;
  userId?: string;
  username?: string;
  connectedAt?: number;
  lastPingTime?: number;
  uptime?: number;
}

/**
 * Connects to the Socket.IO server.
 * Returns the socket instance and utility functions.
 */
// Cache the socket instance to avoid multiple connections
let socketInstance: ReturnType<typeof createSocketInstance> | null = null;

let kucoinStatus: {
  status: string;
  message: string;
  timestamp: number;
  service: string;
} | null = null;

let isSocketConnected = false;

const RECONNECT_ATTEMPTS = {
  tryCount: 5,
};

const createSocketInstance = () => {
  if (!API_ENDPOINTS.ws) {
    return null;
  }

  // console.log('🔌 Testing Socket.IO connection to', API_ENDPOINTS.ws);
  const socket = io(API_ENDPOINTS.ws, {
    transports: ['websocket'],
    auth: {
      token: getAccessToken()
    }
  });

  socket.on('connect', () => {
    console.log('✅ Connected to Socket.IO server!');
    console.log('🔑 Socket ID:', socket.id);
  });

  socket.on('connected', (data: any) => {
    isSocketConnected = true;
    console.log('🎉 Server welcome message:', data);
  });

  socket.on('authenticated', (data: any) => {
    console.log('🔓 Authenticated successfully:', data);
  });

  socket.on('pong', (data: any) => {
    console.log('🏓 Pong received:', data);
  });

  socket.on('error', (error: any) => {
    isSocketConnected = false;
    RECONNECT_ATTEMPTS.tryCount -= 1; // Reset on error
    console.error('❌ Socket error:', error);
  });

  socket.on('disconnect', (reason: any) => {
    isSocketConnected = false;
    console.log('🔌 Disconnected:', reason);
    if (RECONNECT_ATTEMPTS.tryCount <= 0) {
      console.error('❌ Max reconnect attempts reached. Not attempting to reconnect.');
      return;
    }
    // Optionally attempt to reconnect
    socket.connect();
  });

  // Order management functions
  const addOrder = (orderData: OrderData): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      // Set up response handler
      const responseHandler = (data: any) => {
        if (data.requestId === requestId) {
          socket.off('orderAdded', responseHandler);
          socket.off('orderError', errorHandler);
          resolve({
            success: true,
            data: data.response,
            timestamp: Date.now()
          });
        }
      };

      const errorHandler = (data: any) => {
        socket.off('orderAdded', responseHandler);
        socket.off('orderError', errorHandler);
        resolve({
          success: false,
          error: data.error,
          timestamp: Date.now()
        });
      };

      socket.on('orderAdded', responseHandler);
      socket.on('orderError', errorHandler);

      // Send order request
      socket.emit('addOrder', { orderData, requestId });

      // Timeout after 30 seconds
      setTimeout(() => {
        socket.off('orderAdded', responseHandler);
        socket.off('orderError', errorHandler);
        resolve({
          success: false,
          error: 'Order request timeout',
          timestamp: Date.now()
        });
      }, 30000);
    });
  };

  const cancelOrder = (cancelData: CancelOrderData, cb = () => { }): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      // Set up response handler
      const responseHandler = (data: any) => {
        console.log(data);
        console.log(requestId);
        console.log(data?.response?.data?.orderId === cancelData?.orderId);
        if (data?.response?.data?.orderId === cancelData?.orderId) {
          socket.off('orderCanceled', responseHandler);
          socket.off('orderError', errorHandler);
          cb();
          resolve({
            success: true,
            data: data.response,
            timestamp: Date.now()
          });
        }
      };

      const errorHandler = (data: any) => {
        socket.off('orderCanceled', responseHandler);
        socket.off('orderError', errorHandler);
        resolve({
          success: false,
          error: data.error,
          timestamp: Date.now()
        });
      };

      socket.on('orderCanceled', responseHandler);
      socket.on('orderError', errorHandler);

      // Send cancel request
      socket.emit('cancelOrder', { cancelData, requestId });

      // Timeout after 30 seconds
      setTimeout(() => {
        socket.off('orderCanceled', responseHandler);
        socket.off('orderError', errorHandler);
        resolve({
          success: false,
          error: 'Cancel request timeout',
          timestamp: Date.now()
        });
      }, 30000);
    });
  };

  const getConnectionStatus = (): Promise<ConnectionStatus> => {
    return new Promise((resolve) => {
      const handler = (data: ConnectionStatus) => {
        socket.off('connectionStatus', handler);
        resolve(data);
      };

      socket.on('connectionStatus', handler);
      socket.emit('getConnectionStatus', {});

      // Timeout after 5 seconds
      setTimeout(() => {
        socket.off('connectionStatus', handler);
        resolve({ authenticated: false });
      }, 5000);
    });
  };

  const subscribe = (channels: string[]): void => {
    socket.emit('subscribe', { channels }, (response: any) => {
      console.log('📋 Subscribe response:', response);
    });
  };

  const unsubscribe = (channels: string[]): void => {
    socket.emit('unsubscribe', { channels }, (response: any) => {
      console.log('📋 Unsubscribe response:', response);
    });
  };

  const ping = (): void => {
    if (socket.connected) {
      console.log('📤 Sending ping...');
      socket.emit('ping', {}, (response: any) => {
        console.log('📨 Ping response:', response);
      });
    }
  };

  // Handle connection errors
  socket.on('connect_error', (error: any) => {
    console.error('❌ Connection error:', error.message);
    console.error('🔍 Error details:', error);
  });

  // Order event handlers
  socket.on('orderAdded', (data: any) => {
    console.log('✅ Order added:', data);
  });

  socket.on('orderCanceled', (data: any) => {
    console.log('✅ Order canceled:', data);
  });

  socket.on('orderError', (data: any) => {
    console.error('❌ Order error:', data);
  });

  socket.on('kucoinConnectionStatus', (data: {
    status: string,
    message: string,
    timestamp: number,
    service: string;
  }) => {
    kucoinStatus = data;
    console.error('❌ KuCoin connection status:', data);
  });

  const getKucoinStatus = () => kucoinStatus;

  const getSocketConnected = () => isSocketConnected;

  return {
    socket,
    ping,
    addOrder,
    cancelOrder,
    getConnectionStatus,
    getKucoinStatus,
    subscribe,
    unsubscribe,
    getSocketConnected
  };
};

export const connectSocket = () => {
  if (!socketInstance) {
    socketInstance = createSocketInstance();
  }
  return socketInstance;
};

// Example usage:
/*
const { socket, ping, addOrder, cancelOrder, getConnectionStatus, subscribe } = connectSocket();

// Add order example
const orderResponse = await addOrder({
  symbol: 'BTC-USDT',
  side: 'buy',
  type: 'limit',
  size: '0.001',
  price: '50000'
});

// Cancel order example
const cancelResponse = await cancelOrder({
  orderId: '123456789'
});

// Subscribe to updates
subscribe(['ticker:BTC-USDT', 'orderUpdate']);

// Get connection status
const status = await getConnectionStatus();
*/
